import { CircleXmarkRegular } from "@icons";
import * as RadixPopover from "@radix-ui/react-popover";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  ComparisonOperator,
  CompoundCondition,
  CompoundFilterGroup,
  FilterMenuItem,
} from "../menu/types";
import ConditionSlot from "./condition-slot";
import {
  DATE_OPERATOR_LABEL_KEYS,
  NUMERIC_OPERATOR_LABEL_KEYS,
  TEXT_OPERATOR_LABEL_KEYS,
} from "./constants";

function resolveTextValues(
  condition: CompoundCondition & { type: "TEXT" },
  menuItems: FilterMenuItem[],
): string {
  const menuItem = menuItems.find((m) => m.name === condition.fieldName);
  if (menuItem?.type === "TEXT") {
    const optionsMap = new Map(menuItem.options.map((o) => [o.value, o.label]));
    return condition.value
      .map((v) => {
        const label = optionsMap.get(v);
        return typeof label === "string" ? label : v;
      })
      .join(", ");
  }
  return condition.value.join(", ");
}

function getConditionSummary(
  condition: CompoundCondition,
  t: (key: string) => string,
  menuItems: FilterMenuItem[],
): string {
  const field = typeof condition.label === "string" ? condition.label : "Field";
  let op: string;
  let val: string;

  switch (condition.type) {
    case "TEXT":
      op = t(TEXT_OPERATOR_LABEL_KEYS[condition.operator]);
      val = resolveTextValues(condition, menuItems);
      break;
    case "DATE":
      op = t(
        DATE_OPERATOR_LABEL_KEYS[condition.operator as ComparisonOperator],
      ).toLowerCase();
      val = condition.value;
      break;
    case "NUMERIC":
      op = t(
        NUMERIC_OPERATOR_LABEL_KEYS[condition.operator as ComparisonOperator],
      ).toLowerCase();
      val = condition.value;
      break;
    case "BOOLEAN":
      op = t("textOperatorIs");
      val = t(condition.value === "1" ? "booleanYes" : "booleanNo");
      break;
  }

  return `${field} ${op} ${val}`;
}

export default function CompoundFilterChip({
  group,
  filterMenuItemGroups,
  onRemoveGroup,
  onSetCondition,
  onAddCondition,
  onRemoveCondition,
  onToggleLogic,
  logicToggleLocked = false,
}: {
  group: CompoundFilterGroup;
  filterMenuItemGroups: FilterMenuItem[][];
  onRemoveGroup: (groupId: string) => void;
  onSetCondition: (
    groupId: string,
    index: number,
    condition: CompoundCondition,
  ) => void;
  onAddCondition: (groupId: string, condition: CompoundCondition) => void;
  onRemoveCondition: (groupId: string, conditionIndex: number) => void;
  onToggleLogic: (groupId: string) => void;
  logicToggleLocked?: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const condition0 = group.conditions[0] ?? null;
  const condition1 = group.conditions[1] ?? null;
  const allMenuItems = filterMenuItemGroups.flat();

  const logic = group.logic.toUpperCase();
  let chipLabel: string;
  if (condition0 && condition1) {
    chipLabel = `${getConditionSummary(condition0, t, allMenuItems)} ${logic} ${getConditionSummary(condition1, t, allMenuItems)}`;
  } else if (condition0) {
    chipLabel = `${getConditionSummary(condition0, t, allMenuItems)} ${logic} ...`;
  } else {
    chipLabel = `${logic} ${t("compoundFilter").toLowerCase()}`;
  }

  return (
    <RadixPopover.Root open={open} onOpenChange={setOpen}>
      <RadixPopover.Trigger asChild>
        <div className="flex shrink-0 items-center gap-1 rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-xs text-gray-700 cursor-pointer hover:bg-gray-100">
          <span className="max-w-64 truncate">{chipLabel}</span>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRemoveGroup(group.id);
            }}
            className="ml-1 flex items-center text-gray-400 hover:text-gray-600"
            aria-label="Remove compound filter"
          >
            <CircleXmarkRegular />
          </button>
        </div>
      </RadixPopover.Trigger>
      <RadixPopover.Portal>
        <RadixPopover.Content
          className="rounded-md border border-gray-200 bg-white p-3 shadow-lg"
          sideOffset={4}
          align="start"
        >
          <div className="flex flex-col items-start gap-2">
            <ConditionSlot
              condition={condition0}
              filterMenuItemGroups={filterMenuItemGroups}
              onSetCondition={(c) =>
                condition0
                  ? onSetCondition(group.id, 0, c)
                  : onAddCondition(group.id, c)
              }
              onRemove={
                condition0 ? () => onRemoveCondition(group.id, 0) : undefined
              }
            />
            <button
              type="button"
              onClick={() => !logicToggleLocked && onToggleLogic(group.id)}
              disabled={logicToggleLocked}
              className="rounded-full bg-gray-100 px-2.5 py-0.5 text-[10px] font-medium uppercase text-gray-500 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 hover:text-gray-700"
            >
              {group.logic}
            </button>
            <ConditionSlot
              condition={condition1}
              filterMenuItemGroups={filterMenuItemGroups}
              onSetCondition={(c) =>
                condition1
                  ? onSetCondition(group.id, 1, c)
                  : onAddCondition(group.id, c)
              }
              onRemove={
                condition1 ? () => onRemoveCondition(group.id, 1) : undefined
              }
            />
          </div>
        </RadixPopover.Content>
      </RadixPopover.Portal>
    </RadixPopover.Root>
  );
}
